/**
 * Icon utilities for creating SVG icons
 */

export interface IconProps {
  pathData: string
  className?: string
  strokeWidth?: string
  fill?: string
}

/**
 * Creates an SVG icon element
 */
export function createIcon(pathData: string, className = 'w-full h-full'): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('fill', 'none')
  svg.setAttribute('stroke', 'currentColor')
  svg.setAttribute('stroke-width', '2')
  svg.setAttribute('stroke-linecap', 'round')
  svg.setAttribute('stroke-linejoin', 'round')
  svg.setAttribute('class', className)

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', pathData)

  svg.appendChild(path)
  return svg
}

/**
 * Common icon paths
 */
export const IconPaths = {
  // Location/Map
  location: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',

  // Check/Success
  check: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3',

  // Email
  email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',

  // Phone
  phone: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',

  // Energy/Zap
  zap: 'M13 10V3L4 14h7v7l9-11h-7z',

  // Lightning/Fast
  lightning: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',

  // Shield/Reliability
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',

  // Box/Modular
  box: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z',

  // Arrow down (for dropdown)
  arrowDown: 'M19 9l-7 7-7-7',

  // Send/Paper plane
  send: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
} as const

/**
 * Creates an icon with a circular background container
 */
export function createIconWithBackground(
  iconPath: string,
  bgColorClass: string,
  iconColorClass: string,
  sizeClass = 'w-12 h-12'
): HTMLElement {
  const container = document.createElement('div')
  container.className = `${sizeClass} rounded-full flex items-center justify-center flex-shrink-0 ${bgColorClass}`

  const iconWrapper = document.createElement('span')
  iconWrapper.className = `w-6 h-6 ${iconColorClass}`
  iconWrapper.appendChild(createIcon(iconPath))

  container.appendChild(iconWrapper)
  return container
}
